/**
 * Hook for backing up MSSQL databases to .bak files (native T-SQL).
 * Uses TanStack Query mutation for async state management.
 */

import { useMutation } from '@tanstack/react-query';
import { mssqlBackupBak } from '../services/backupRestoreApi';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import type { MssqlBackupBakRequest, MssqlBackupBakResult } from '../types/backupRestore';
import { useAnalytics } from '../../../core/analytics';

type UseBackupBakOptions = {
  onOperationCreated?: (operationId: string) => void;
  onSuccess?: (result: MssqlBackupBakResult, operationId: string) => void;
  onError?: (error: Error) => void;
};

/**
 * Hook for initiating MSSQL .bak backup operations.
 * Tracks progress in the backup/restore store.
 */
export function useBackupBak(options: UseBackupBakOptions = {}) {
  const { addOperation, updateOperation } = useBackupRestoreStore();
  const { track } = useAnalytics();

  const mutation = useMutation({
    mutationFn: async (request: MssqlBackupBakRequest): Promise<{ result: MssqlBackupBakResult; operationId: string }> => {
      // Track backup initiation
      track('feature:backup_restore', { engine: 'mssql' });

      // Add operation to store before starting
      const operationId = addOperation({
        type: 'backup-bak',
        connectionId: request.connectionId,
        databaseName: request.databaseName,
        filePath: request.serverFilePath,
      });

      options.onOperationCreated?.(operationId);

      try {
        // Update status to in progress
        updateOperation(operationId, {
          taskId: operationId,
          status: 'inProgress',
          message: 'Backup starting...',
        });

        const result = await mssqlBackupBak({ ...request, taskId: operationId });

        if (result.success) {
          updateOperation(operationId, {
            status: 'succeeded',
            message: `Backup completed in ${result.duration ? `${(result.duration / 1000).toFixed(1)}s` : 'unknown time'}`,
            completedAt: new Date().toISOString(),
          });
        } else if (result.cancelled) {
          updateOperation(operationId, {
            status: 'cancelled',
            message: 'Cancelled by user',
            completedAt: new Date().toISOString(),
          });
        } else {
          updateOperation(operationId, {
            status: 'failed',
            error: result.error,
            completedAt: new Date().toISOString(),
          });
        }

        return { result, operationId };
      } catch (error) {
        updateOperation(operationId, {
          status: 'failed',
          error: error instanceof Error ? error.message : 'Unknown error',
          completedAt: new Date().toISOString(),
        });
        throw error;
      }
    },
    onSuccess: (data) => {
      options.onSuccess?.(data.result, data.operationId);
    },
    onError: (error: Error) => {
      options.onError?.(error);
    },
  });

  return {
    backupDatabase: mutation.mutate,
    backupDatabaseAsync: mutation.mutateAsync,
    isBackingUp: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
